import { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { useAppMode } from '../../../contexts/AppModeContext';
import {
  Send, Sparkles, X, Calendar, Clock, User, AlertTriangle,
  CheckCircle2, Minimize2, Maximize2, Bot,
} from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_MESSAGES = [
  { role: 'assistant', text: "Hi! I'm your scheduling assistant. I can help you reschedule appointments, block dates, manage provider availability, and optimize your calendar. What would you like to do?" },
];

const AI_RESPONSES = {
  reschedule: [
    { role: 'assistant', text: "I can help with that. Which patient's appointment would you like to reschedule?" },
    { role: 'assistant', text: "I found John Smith's 9:00 AM Follow-up with Dr. Chen. When would you like to move it to?", delay: 800 },
  ],
  block: [
    { role: 'assistant', text: "Sure, I'll block that time. Which provider and date range should I block?" },
    { role: 'assistant', text: "Got it. I'll block Dr. Chen's calendar on Friday April 18th from 1:00 PM - 5:00 PM. Reason: Administrative.\n\nThis will affect 3 existing appointments. Shall I:\n1. Auto-reschedule them to next available slots\n2. Send patients a reschedule request\n3. Cancel and notify", delay: 800 },
  ],
  noshow: [
    { role: 'assistant', text: "David Wilson has a 42% no-show risk for his 2:00 PM appointment. I've already sent 2 reminders. Would you like me to:\n1. Make another confirmation call\n2. Double-book the slot (his history suggests 3/5 no-shows)\n3. Move a waitlist patient to standby" },
  ],
  fill: [
    { role: 'assistant', text: "The 11:30 AM slot is open. I have 3 waitlist matches ranked by priority:\n\n1. **Jennifer Lee** — Match 5/5 — HbA1c due — Follow-up\n2. **Amanda Rodriguez** — Match 4/5 — Annual Wellness — $250 covered\n3. **Thomas Parker** — Match 3/5 — Sick Visit\n\nShall I contact Jennifer Lee first? I can call and text simultaneously." },
  ],
  hours: [
    { role: 'assistant', text: "I can update provider hours. Which provider's schedule would you like to modify? Current active providers:\n• Dr. Chen — Mon-Fri 8:00-5:00\n• Dr. Wilson — Mon-Sat 8:30-4:30\n• Dr. Roberts — Mon-Fri 9:00-4:00" },
  ],
};

const detectIntent = (text) => {
  const t = text.toLowerCase();
  if (t.includes('reschedule') || t.includes('move')) return 'reschedule';
  if (t.includes('block') || t.includes('off') || t.includes('close') || t.includes('cancel')) return 'block';
  if (t.includes('no-show') || t.includes('no show') || t.includes('noshow') || t.includes('risk')) return 'noshow';
  if (t.includes('fill') || t.includes('open slot') || t.includes('waitlist')) return 'fill';
  if (t.includes('hour') || t.includes('availability') || t.includes('schedule') || t.includes('provider time')) return 'hours';
  return null;
};

const SchedulerAIChat = ({ compact = false }) => {
  const { isDemo } = useAppMode();
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  if (!isDemo) {
    // Keep the AI chat out of the real-mode render path entirely until a live
    // scheduler-AI endpoint is wired. Prevents demo patient names leaking.
    return null;
  }

  const sendMessage = () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setInput('');
    setThinking(true);

    const intent = detectIntent(userMsg);
    const responses = intent ? (AI_RESPONSES[intent] || []) : [];

    if (responses.length > 0) {
      setTimeout(() => {
        setMessages(prev => [...prev, responses[0]]);
        setThinking(false);
        if (responses.length > 1) {
          setTimeout(() => {
            setMessages(prev => [...prev, responses[1]]);
          }, responses[1].delay || 1500);
        }
      }, 800);
    } else {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: `I can help with that. Let me look into "${userMsg}" for you. I can:\n• Reschedule or move appointments\n• Block dates or time ranges\n• Fill open slots from waitlist\n• Update provider hours\n• Handle no-show risks\n\nCould you be more specific about what you'd like me to do?`
        }]);
        setThinking(false);
      }, 600);
    }
  };

  if (minimized) {
    return (
      <button onClick={() => setMinimized(false)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
        style={{ cursor: 'pointer' }} data-testid="ai-chat-minimized">
        <Sparkles className="h-3.5 w-3.5 text-violet-500" />
        <span className="text-xs font-medium text-slate-700">AI Assistant</span>
        <Maximize2 className="h-3 w-3 text-slate-400" />
      </button>
    );
  }

  return (
    <div className={cn("rounded-xl border border-slate-200 bg-white flex flex-col overflow-hidden", compact ? "h-[360px]" : "h-[440px]")} data-testid="ai-chat-widget">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-violet-100 flex items-center justify-center">
            <Sparkles className="h-3 w-3 text-violet-600" />
          </div>
          <div>
            <p className="text-[11px] font-semibold text-slate-800">Schedule AI</p>
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="text-[8px] text-emerald-600">Active</span>
            </div>
          </div>
        </div>
        <button onClick={() => setMinimized(true)} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="ai-chat-minimize">
          <Minimize2 className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2" data-testid="ai-chat-messages">
        {messages.map((msg, i) => (
          <div key={i} className={cn("flex", msg.role === 'user' ? "justify-end" : "justify-start")}>
            <div className={cn("max-w-[85%] rounded-xl px-3 py-2 text-[11px] leading-relaxed whitespace-pre-line",
              msg.role === 'user' ? "bg-[#111] text-white rounded-br-sm" : "bg-slate-100 text-slate-700 rounded-bl-sm"
            )} data-testid={`ai-msg-${i}`}>
              {msg.text}
            </div>
          </div>
        ))}
        {thinking && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-xl px-3 py-2 flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick actions */}
      <div className="px-3 py-1.5 flex gap-1 flex-wrap border-t border-slate-50">
        {['Reschedule appt', 'Block a date', 'Fill open slots', 'No-show risks'].map(q => (
          <button key={q} onClick={() => { setInput(q); }}
            className="px-2 py-1 rounded-md bg-slate-50 text-[9px] text-slate-500 hover:bg-slate-100 transition-colors"
            style={{ border: 'none', cursor: 'pointer' }} data-testid={`ai-quick-${q.replace(/\s/g, '-').toLowerCase()}`}>{q}</button>
        ))}
      </div>

      {/* Input */}
      <div className="px-3 py-2 border-t border-slate-100">
        <div className="flex items-center gap-2">
          <input type="text" value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask me anything about scheduling..."
            className="flex-1 text-[11px] bg-slate-50 rounded-lg px-3 py-2 outline-none border border-slate-200 focus:border-slate-300 transition-colors"
            data-testid="ai-chat-input" />
          <Button size="icon" className="h-8 w-8 rounded-lg bg-[#111] hover:bg-[#222]" onClick={sendMessage} disabled={!input.trim()} data-testid="ai-chat-send">
            <Send className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SchedulerAIChat;
